import React, { useMemo } from "react";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField
} from "../forms/GlobalFormFields";
import "./globalDirectory.css";

/**
 * Row 1: search text, column select, value select, clear.
 * Row 2: pass `secondaryStart` / `secondaryEnd` (e.g. column picker + export menu).
 *
 * When `gridColumnDefs` is a non-empty array, the Column dropdown is built from it
 * (`id`/`value` + `label`), matching the grid’s column definitions. Otherwise `columnOptions` is used.
 */
export default function GlobalDirectoryFiltersSection({
  className = "",
  /** Disables search and both dropdowns in row 1 (secondary row not affected). */
  disabled = false,
  searchLabel = "Search",
  searchPlaceholder = "",
  searchValue = "",
  onSearchChange,
  columnLabel = "Column",
  columnValue = "",
  onColumnChange,
  columnOptions = [],
  /** @type {{ id?: string, value?: string, label: string }[]|null} */
  gridColumnDefs = null,
  columnPlaceholderOption = "Select",
  valueLabel = "Value",
  valueValue = "",
  onValueChange,
  valueOptions = [],
  valuePlaceholderOption = "All",
  onClear,
  clearFilterLabel = "Clear filter",
  /** When true, disables only the Value select (e.g. while loading options). */
  valueSelectDisabled = false,
  secondaryStart = null,
  secondaryEnd = null
}) {
  const resolvedColumnOptions = useMemo(() => {
    if (Array.isArray(gridColumnDefs) && gridColumnDefs.length > 0) {
      return gridColumnDefs
        .map((d) => {
          const value = String(d.id ?? d.value ?? "").trim();
          const label = String(d.label ?? d.id ?? d.value ?? "").trim();
          return value ? { value, label: label || value } : null;
        })
        .filter(Boolean);
    }
    return columnOptions;
  }, [gridColumnDefs, columnOptions]);

  return (
    <div className={`gds-filters ub-directory-filters ${className}`.trim()}>
      <div className="gds-filters__row">
        <div className="gds-filters__grow">
          <GlobalTextField
            id="gds-directory-search"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            disabled={disabled}
          />
        </div>
        <div className="gds-filters__col">
          <GlobalSelectField
            id="gds-directory-column"
            label={columnLabel}
            value={columnValue}
            onChange={onColumnChange}
            placeholderOption={columnPlaceholderOption}
            options={resolvedColumnOptions}
            disabled={disabled}
          />
        </div>
        <div className="gds-filters__col gds-filters__col--wide">
          <GlobalSelectField
            id="gds-directory-value"
            label={valueLabel}
            value={valueValue}
            onChange={onValueChange}
            placeholderOption={valuePlaceholderOption}
            options={valueOptions}
            disabled={disabled || valueSelectDisabled}
          />
        </div>
        <div className="gds-filters__actions">
          <GlobalButton type="button" size="sm" variant="ghost" onClick={onClear} disabled={disabled}>
            {clearFilterLabel}
          </GlobalButton>
        </div>
      </div>
      {secondaryStart != null || secondaryEnd != null ? (
        <div className="gds-filters__row gds-filters__row--secondary">
          <div className="gds-directory-toolbar-track">
            {secondaryStart != null ? (
              <div className="gds-directory-toolbar-bar">
                <div className="gds-directory-toolbar-bar__start">
                  {secondaryStart}
                </div>
                <div
                  className="gds-directory-toolbar-bar__filler"
                  aria-hidden
                />
              </div>
            ) : (
              <div className="gds-directory-toolbar-track__spacer" aria-hidden />
            )}
            {secondaryEnd != null ? (
              <div className="gds-directory-toolbar-track__end">
                {secondaryEnd}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
